/**
 * Map a job (office camelCase or field snake_case) and a log into the
 * shift-log form's plain options, so both surfaces offer the same rows: every
 * crew member, every owned unit, every rent-in (the form then offers a rent-in
 * only on its days).
 */
import type { WtFieldJobDetail, WtFieldShiftLog, WtJobDetail, WtShiftLog } from '../../types/well-testing';
import type { LogCrewOption, LogUnitOption, ShiftLogValues } from './ShiftLogEditor';

export function officeLogOptions(job: WtJobDetail): { crew: LogCrewOption[]; units: LogUnitOption[] } {
  return {
    crew: job.crew.map((c) => ({ userId: c.userId, name: c.userName ?? '—', detail: [c.crewRoleLabel, c.shiftLabel].filter(Boolean).join(' · ') || null })),
    units: [
      ...job.equipment.map((e) => ({
        key: `owned:${e.equipmentId}`,
        kind: 'owned' as const,
        equipmentId: e.equipmentId,
        rentInId: null,
        name: e.equipmentName ?? e.unitNumber ?? '—',
        detail: [e.unitNumber, e.componentKindLabel].filter(Boolean).join(' · ') || null,
      })),
      ...job.rentIns.map((r) => ({
        key: `rent_in:${r.id}`,
        kind: 'rent_in' as const,
        equipmentId: r.equipmentId,
        rentInId: r.id,
        name: r.description || r.vendorName,
        detail: [r.vendorName, r.unitNumber].filter(Boolean).join(' · ') || null,
        onRentOn: r.onRentOn,
        offRentOn: r.offRentOn,
      })),
    ],
  };
}

export function fieldLogOptions(job: WtFieldJobDetail): { crew: LogCrewOption[]; units: LogUnitOption[] } {
  return {
    crew: (job.crew ?? []).map((c) => ({ userId: c.user_id, name: c.user_name ?? '—', detail: [c.crew_role_label, c.shift_label].filter(Boolean).join(' · ') || null })),
    units: [
      ...(job.equipment ?? []).map((e) => ({
        key: `owned:${e.equipment_id}`,
        kind: 'owned' as const,
        equipmentId: e.equipment_id,
        rentInId: null,
        name: e.equipment_name ?? e.unit_number ?? '—',
        detail: [e.unit_number, e.component_kind_label].filter(Boolean).join(' · ') || null,
      })),
      ...(job.rent_ins ?? []).map((r) => ({
        key: `rent_in:${r.id}`,
        kind: 'rent_in' as const,
        equipmentId: r.equipment_id,
        rentInId: r.id,
        name: r.description || r.vendor_name,
        detail: [r.vendor_name, r.unit_number].filter(Boolean).join(' · ') || null,
        onRentOn: r.on_rent_on,
        offRentOn: r.off_rent_on,
      })),
    ],
  };
}

export function officeLogValues(log: WtShiftLog | null): ShiftLogValues | null {
  if (!log) return null;
  return {
    crewPresent: (log.crewPresent ?? []).map((c) => ({ userId: c.userId, name: c.userName })),
    hours: log.hours,
    standbyHours: log.standbyHours,
    safetyNotes: log.safetyNotes,
    handoverNote: log.handoverNote,
    notes: log.notes,
    units: (log.units ?? []).map((u) => ({
      kind: u.kind,
      equipmentId: u.equipmentId,
      rentInId: u.rentInId,
      name: u.unitName,
      hours: u.hours,
      standbyHours: u.standbyHours,
      hourMeterEnd: u.hourMeterEnd,
      notes: u.notes,
    })),
  };
}

export function fieldLogValues(log: WtFieldShiftLog | null): ShiftLogValues | null {
  if (!log) return null;
  return {
    crewPresent: (log.crew_present ?? []).map((c) => ({ userId: c.user_id, name: c.user_name })),
    hours: log.hours,
    standbyHours: log.standby_hours,
    safetyNotes: log.safety_notes,
    handoverNote: log.handover_note,
    notes: log.notes,
    units: (log.units ?? []).map((u) => ({
      kind: u.kind,
      equipmentId: u.equipment_id,
      rentInId: u.rent_in_id,
      name: u.unit_name,
      hours: u.hours,
      standbyHours: u.standby_hours,
      hourMeterEnd: u.hour_meter_end,
      notes: u.notes,
    })),
  };
}
